import React, { useState } from "react";

export default function ContactForm({
  defaultService = "",
  calculatorDetails = null,
  title = "Kontakt os",
  subtitle = "Du kan let og hurtigt sende os en besked i nedenstående formular.",
}) {
  const [formData, setFormData] = useState({
    navn: "",
    telefon: "",
    email: "",
    ydelse: defaultService,
    adresse: "",
    beskrivelse: calculatorDetails
      ? `Forespørgsel fra Prisberegner:\n${calculatorDetails}\n\n`
      : "",
    website_url: "", // Honeypot
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  const [errors, setErrors] = useState({});

  function validate() {
    const errs = {};
    if (!formData.navn.trim()) errs.navn = "Angiv venligst dit navn";
    if (!formData.telefon.trim()) {
      errs.telefon = "Angiv venligst dit telefonnummer";
    } else if (!/^[\d\s+()-]{8,}$/.test(formData.telefon.trim())) {
      errs.telefon = "Angiv et gyldigt telefonnummer";
    }
    if (!formData.email.trim()) {
      errs.email = "Angiv venligst din e-mail";
    } else if (!/\S+@\S+\.\S+/.test(formData.email.trim())) {
      errs.email = "Angiv en gyldig e-mailadresse";
    }
    if (!formData.beskrivelse.trim()) {
      errs.beskrivelse = "Skriv venligst en kort beskrivelse af din opgave";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Der opstod en fejl under afsendelsen. Prøv venligst igen.");
      }

      setStatus({ submitting: false, success: true, error: null });

      // Google Tag / GTM conversion event
      if (typeof window !== "undefined" && window.dataLayer) {
        window.dataLayer.push({
          event: "generate_lead",
          form_name: "Kontaktformular",
          service: formData.ydelse || "Ikke angivet",
        });
      }

      setFormData({
        navn: "",
        telefon: "",
        email: "",
        ydelse: "",
        adresse: "",
        beskrivelse: "",
        website_url: "",
      });
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: err.message || "Kunne ikke sende formularen. Ring evt. direkte på 42 49 95 95.",
      });
    }
  }

  return (
    <div className="bg-white rounded-2xl p-6 sm:p-8 md:p-10 border border-stone-200/80 shadow-sm relative overflow-hidden">
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-stone-900 tracking-tight">{title}</h3>
        {subtitle && (
          <p className="text-stone-600 text-sm mt-1.5 leading-relaxed">{subtitle}</p>
        )}
      </div>

      {status.success ? (
        <div className="p-6 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 text-center space-y-3 animate-in fade-in">
          <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto text-xl">
            <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
          </div>
          <h4 className="text-lg font-bold">Mange tak for din henvendelse!</h4>
          <p className="text-sm text-emerald-800">
            Vi har modtaget din besked og vender tilbage hurtigst muligt – typisk inden for 24 timer.
          </p>
          <div className="pt-2">
            <button
              onClick={() => setStatus({ submitting: false, success: false, error: null })}
              className="px-5 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold rounded-full transition-colors cursor-pointer"
            >
              Send en ny besked
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* Honeypot field - ABSOLUTELY REQUIRED & SR-ONLY */}
          <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Venligst lad dette felt være tomt</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Navn */}
            <div>
              <label htmlFor="navn" className="block text-xs font-semibold text-stone-700 mb-1">
                Navn <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="navn"
                value={formData.navn}
                onChange={(e) => {
                  setFormData({ ...formData, navn: e.target.value });
                  if (errors.navn) setErrors({ ...errors, navn: null });
                }}
                placeholder="F.eks. Anders Jensen"
                className={`w-full px-3.5 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 transition-all ${
                  errors.navn
                    ? "border-red-400 focus:ring-red-200 bg-red-50/30"
                    : "border-stone-300 focus:border-[#165B4C] focus:ring-[#165B4C]/20 bg-stone-50/40"
                }`}
              />
              {errors.navn && <p className="text-xs text-red-600 mt-1">{errors.navn}</p>}
            </div>

            {/* Telefonnummer */}
            <div>
              <label htmlFor="telefon" className="block text-xs font-semibold text-stone-700 mb-1">
                Telefonnummer <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="telefon"
                value={formData.telefon}
                onChange={(e) => {
                  setFormData({ ...formData, telefon: e.target.value });
                  if (errors.telefon) setErrors({ ...errors, telefon: null });
                }}
                placeholder="F.eks. 42 49 95 95"
                className={`w-full px-3.5 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 transition-all ${
                  errors.telefon
                    ? "border-red-400 focus:ring-red-200 bg-red-50/30"
                    : "border-stone-300 focus:border-[#165B4C] focus:ring-[#165B4C]/20 bg-stone-50/40"
                }`}
              />
              {errors.telefon && <p className="text-xs text-red-600 mt-1">{errors.telefon}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-xs font-semibold text-stone-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  if (errors.email) setErrors({ ...errors, email: null });
                }}
                placeholder="din@email.dk"
                className={`w-full px-3.5 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 transition-all ${
                  errors.email
                    ? "border-red-400 focus:ring-red-200 bg-red-50/30"
                    : "border-stone-300 focus:border-[#165B4C] focus:ring-[#165B4C]/20 bg-stone-50/40"
                }`}
              />
              {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
            </div>

            {/* Ydelse */}
            <div>
              <label htmlFor="ydelse" className="block text-xs font-semibold text-stone-700 mb-1">
                Hvilken ydelse drejer det sig om?
              </label>
              <select
                id="ydelse"
                value={formData.ydelse}
                onChange={(e) => setFormData({ ...formData, ydelse: e.target.value })}
                className="w-full px-3.5 py-2.5 rounded-xl border border-stone-300 bg-stone-50/40 text-sm focus:border-[#165B4C] focus:ring-2 focus:ring-[#165B4C]/20 focus:outline-none"
              >
                <option value="">Vælg ydelse (valgfrit)</option>
                <option value="Energimærkning">Energimærkning</option>
                <option value="Trykprøvning">Trykprøvning / BlowerDoor</option>
                <option value="Termografi">Termografi</option>
                <option value="Energiramme">Energiramme beregning</option>
                <option value="Kombineret løsning">Kombineret rådgivning</option>
                <option value="Andet">Andet spørgsmål</option>
              </select>
            </div>
          </div>

          {/* By eller Adresse */}
          <div>
            <label htmlFor="adresse" className="block text-xs font-semibold text-stone-700 mb-1">
              Ejendommens by eller postnummer (valgfrit)
            </label>
            <input
              type="text"
              id="adresse"
              value={formData.adresse}
              onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
              placeholder="F.eks. 8700 Horsens eller 7100 Vejle"
              className="w-full px-3.5 py-2.5 rounded-xl border border-stone-300 bg-stone-50/40 text-sm focus:border-[#165B4C] focus:ring-2 focus:ring-[#165B4C]/20 focus:outline-none"
            />
          </div>

          {/* Beskrivelse */}
          <div>
            <label htmlFor="beskrivelse" className="block text-xs font-semibold text-stone-700 mb-1">
              Beskrivelse af opgaven <span className="text-red-500">*</span>
            </label>
            <textarea
              id="beskrivelse"
              rows={4}
              value={formData.beskrivelse}
              onChange={(e) => {
                setFormData({ ...formData, beskrivelse: e.target.value });
                if (errors.beskrivelse) setErrors({ ...errors, beskrivelse: null });
              }}
              placeholder="Fortæl kort om boligen (f.eks. type, areal, nybyg/salg eller ønsket tidspunkt)..."
              className={`w-full px-3.5 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 transition-all ${
                errors.beskrivelse
                  ? "border-red-400 focus:ring-red-200 bg-red-50/30"
                  : "border-stone-300 focus:border-[#165B4C] focus:ring-[#165B4C]/20 bg-stone-50/40"
              }`}
            />
            {errors.beskrivelse && (
              <p className="text-xs text-red-600 mt-1">{errors.beskrivelse}</p>
            )}
          </div>

          {status.error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs flex items-center gap-2">
              <i className="fa-solid fa-triangle-exclamation shrink-0" aria-hidden="true"></i>
              <span>{status.error}</span>
            </div>
          )}

          <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
            <button
              type="submit"
              disabled={status.submitting}
              className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-[#165B4C] hover:bg-[#11463A] text-white font-semibold text-sm shadow hover:shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-60 cursor-pointer"
            >
              {status.submitting ? (
                <>
                  <i className="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>
                  <span>Sender besked...</span>
                </>
              ) : (
                <>
                  <span>Send besked</span>
                  <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true"></i>
                </>
              )}
            </button>

            <div className="text-xs text-stone-500 text-center sm:text-right">
              <span className="inline-flex items-center gap-1.5 text-stone-600">
                <i className="fa-solid fa-shield-halved text-emerald-600 text-xs" aria-hidden="true"></i>
                Dine oplysninger behandles fortroligt
              </span>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
